import React from "react";
import { Link } from "react-router-dom";
import { ExternalLinkIcon } from "@heroicons/react/outline";
import { Action, DropdownMenuVariant } from "../../../DropdownMenu";
import { ContextMenu } from "../../ContextMenu";
import { ContextMenuItems } from "../../ContextMenu/types";
import { CardHeader } from "./CardHeader";
import { CardMetadata } from "./CardMetadata";
import { CardBody } from "./CardBody";
import { CardTags } from "./CardTags";
import { CardProps } from "./types";
import { convertActionsToContextMenuItems } from "./utils";

export const Card = <DataT,>({
  onClick,
  link,
  className = "",
  title,
  icon,
  subtitle,
  metadata = [],
  tags = [],
  maxVisibleTags = 3,
  body,
  actions,
  item,
  tagsSeparator,
  useBadge,
  emptyMetadataMessage,
  emptyTagsMessage,
  variant,
  shouldShowEmptyText,
  loadingState,
}: CardProps<DataT>) => {
  const baseClasses =
    "flex flex-col rounded-md border-[1px] border-gray-200 bg-white transition-all duration-200 hover:border-blue-300 cursor-pointer w-full min-h-42 h-full";

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const hasActionableItems = Boolean(actions && actions.length > 0);
  const additionalItems = hasActionableItems
    ? [{ id: "divider", isDivider: true }, ...(actions || [])]
    : [];

  const dropdownMenuItems: Action<DataT>[] = [
    {
      id: "open-in-new-tab",
      label: "Open in new tab",
      icon: ExternalLinkIcon,
      onClick: () => window.open(link, "_blank"),
      variant: DropdownMenuVariant.PRIMARY,
    },
    ...additionalItems,
  ];

  const defaultContextMenuItems: ContextMenuItems<DataT>[] =
    convertActionsToContextMenuItems(dropdownMenuItems as Action<DataT>[]);

  const hasMetadata = metadata.length > 0;
  const hasBody = Boolean(body);
  const hasTags = tags.length > 0;

  return (
    <ContextMenu
      modal={false}
      contextMenuItems={defaultContextMenuItems}
      data={item}
    >
      <Link
        to={link}
        className={`${baseClasses} ${className} group`}
        onClick={handleClick}
      >
        <div className="mt-0.5 flex h-full w-full flex-col px-3 pb-2 pt-1.5 text-gray-900">
          <div className="flex flex-shrink-0 items-start">
            <div className="min-w-0 flex-1">
              <CardHeader
                variant={variant}
                icon={icon}
                loadingState={loadingState}
                title={title}
                item={item}
                subtitle={subtitle}
                actions={actions}
                dropdownMenuItems={dropdownMenuItems}
              />
            </div>
          </div>
          <div
            className={`mx-[40px] flex min-h-0 flex-1 flex-col items-start justify-between ${
              hasMetadata || hasBody || hasTags ? "flex" : "hidden"
            }`}
          >
            <div className="w-full flex-1">
              <CardMetadata
                emptyText={emptyMetadataMessage}
                metadata={metadata}
              />
              <CardBody body={body} />
            </div>
            <div className="w-full flex-shrink-0">
              <CardTags
                emptyText={emptyTagsMessage}
                tags={tags}
                shouldShowEmptyText={shouldShowEmptyText}
                maxVisibleTags={maxVisibleTags}
                separator={tagsSeparator}
                useBadge={useBadge}
              />
            </div>
          </div>
        </div>
      </Link>
    </ContextMenu>
  );
};

export type { MetadataItem } from "./CardMetadata";
export { CardTagsSeparator } from "./CardTags";
export { CardsGrid } from "./CardsGrid";
export { CardVariant } from "./constants";
